// web/src/screens/Reports.tsx — the client's monthly reports (portal spec 17), route /reports[?site=<id>].
// One site at a time (the first live one unless ?site= says otherwise; a picker when there are several), newest month
// first, month pills that scroll sideways inside their own row. Only published reports reach this screen.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Site } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { ReportView, monthLabel, monthName, windowLabel } from '../components/ReportView';

/** Stages whose sites can have reports (a paused or ending site keeps its past months). */
const HAS_REPORTS: Site['stage'][] = ['live', 'legacy', 'paused', 'offboarding'];

export function Reports() {
  const [params, setParams] = useSearchParams();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites() });
  const choices = (sites.data ?? []).filter((s) => HAS_REPORTS.includes(s.stage));
  const wanted = params.get('site');
  const siteId = (wanted && (sites.data ?? []).some((s) => s.id === wanted) ? wanted : null)
    ?? choices.find((s) => s.stage === 'live' || s.stage === 'legacy')?.id ?? choices[0]?.id;
  const q = useQuery({ queryKey: ['reports', siteId], queryFn: () => api.listReports(siteId!), enabled: !!siteId });
  const [i, setI] = useState(0);
  useEffect(() => setI(0), [siteId]); // a different site's list may be shorter

  return (
    <>
      <PageHeader title="Reports" subtitle="What happened on your website each month." />
      {choices.length > 1 && siteId && (
        <label className="mb-4 block text-sm text-muted">Website
          <select value={siteId} onChange={(e) => setParams({ site: e.target.value }, { replace: true })}
            className="mt-1 block min-h-11 w-full min-w-0 rounded-md border border-hair bg-bg px-3 text-ink">
            {choices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      )}
      {sites.isLoading ? <Skeleton variant="card" count={2} /> : !siteId ? (
        <EmptyState icon="bar-chart" title="Reports start once your site is live" body="Each month’s report will show up right here." />
      ) : (
        <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={2} />}
          isEmpty={(r) => r.length === 0}
          empty={{ icon: 'bar-chart', title: 'Your first report is on its way', body: 'We’ll show it here as soon as it’s ready.' }}>
          {(reports) => {
            const r = reports[Math.min(i, reports.length - 1)]!;
            return (
              <div className="flex min-w-0 flex-col gap-4">
                {reports.length > 1 && (
                  <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" role="tablist" aria-label="Month">
                    {reports.map((x, idx) => (
                      <button key={x.id} role="tab" aria-selected={idx === i} onClick={() => setI(idx)}
                        className={`min-h-11 shrink-0 rounded-pill border px-4 text-sm ${idx === i ? 'border-run text-run' : 'border-hair text-muted'}`}>
                        {monthLabel(x.period)}
                      </button>
                    ))}
                  </div>
                )}
                <ReportView doc={r.doc} label={`Your ${monthName(r.period)} update · ${windowLabel(r)}`} />
              </div>
            );
          }}
        </AsyncBoundary>
      )}
    </>
  );
}
